import PubSub from "pubsub-js"

// LocalStorage key for user preferences
const STORAGE_KEY = "local-user-preferences"

// In-memory cache for preferences
let preferencesCache = null

/**
 * Load preferences from localStorage
 * @returns {Object} - Preferences object
 */
const loadPreferences = () => {
  if (preferencesCache !== null) {
    return preferencesCache
  }

  if (typeof window === "undefined") {
    preferencesCache = {}
    return preferencesCache
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    preferencesCache = stored ? JSON.parse(stored) : {}
  } catch (error) {
    console.warn("Failed to load user preferences:", error)
    preferencesCache = {}
  }

  return preferencesCache
}

/**
 * Save preferences to localStorage
 * @param {Object} preferences - Preferences object to save
 */
const savePreferences = (preferences) => {
  preferencesCache = preferences

  if (typeof window === "undefined") {
    return
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
  } catch (error) {
    console.warn("Failed to save user preferences:", error)
  }

  // Publish change notification
  PubSub.publish("local-user-preferences-changed", preferences)
}

/**
 * Get value from preferences using dot notation
 * @param {string} address - Dot notation address (e.g., "ui.ticketBrowser.items")
 * @returns {*} - Value at the address or undefined if not found
 */
const getValueByAddress = (address, preferences) => {
  if (!address || typeof address !== "string") {
    return undefined
  }

  const parts = address.split(".")
  let current = preferences

  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== "object") {
      return undefined
    }
    current = current[part]
  }

  return current
}

/**
 * Set value in preferences using dot notation
 * @param {string} address - Dot notation address (e.g., "ui.ticketBrowser.items")
 * @param {*} value - Value to set
 * @param {Object} preferences - Preferences object to modify
 * @returns {Object} - Modified preferences object
 */
const setValueByAddress = (address, value, preferences) => {
  if (!address || typeof address !== "string") {
    return preferences
  }

  const parts = address.split(".")
  const result = JSON.parse(JSON.stringify(preferences)) // Deep clone
  let current = result

  // Navigate to the parent of the target property
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]
    if (current[part] === undefined || typeof current[part] !== "object" || Array.isArray(current[part])) {
      current[part] = {}
    }
    current = current[part]
  }

  // Set the final property
  const lastPart = parts[parts.length - 1]
  current[lastPart] = value

  return result
}

/**
 * Delete value from preferences using dot notation
 * @param {string} address - Dot notation address
 * @param {Object} preferences - Preferences object to modify
 * @returns {Object} - Modified preferences object
 */
const deleteValueByAddress = (address, preferences) => {
  if (!address || typeof address !== "string") {
    return preferences
  }

  const parts = address.split(".")
  const result = JSON.parse(JSON.stringify(preferences)) // Deep clone
  let current = result

  // Navigate to the parent of the target property
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]
    if (current[part] === undefined || typeof current[part] !== "object" || Array.isArray(current[part])) {
      return result // Path doesn't exist, nothing to delete
    }
    current = current[part]
  }

  // Delete the final property
  const lastPart = parts[parts.length - 1]
  delete current[lastPart]

  return result
}

/**
 * Check if address matches wildcard pattern
 * @param {string} address - Address to check
 * @param {string} pattern - Wildcard pattern (e.g., "ui.*")
 * @returns {boolean} - True if address matches pattern
 */
const matchesWildcard = (address, pattern) => {
  if (pattern.endsWith("*")) {
    const prefix = pattern.slice(0, -1)
    return address.startsWith(prefix)
  }
  return address === pattern
}

/**
 * Get all addresses that match a wildcard pattern
 * @param {string} pattern - Wildcard pattern (e.g., "ui.*")
 * @param {Object} preferences - Preferences object
 * @returns {Array<string>} - Array of matching addresses
 */
const getMatchingAddresses = (pattern, preferences) => {
  const addresses = []

  const traverse = (obj, currentPath = "") => {
    for (const key in obj) {
      const fullPath = currentPath ? `${currentPath}.${key}` : key
      
      if (matchesWildcard(fullPath, pattern)) {
        addresses.push(fullPath)
      }

      if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
        traverse(obj[key], fullPath)
      }
    }
  }

  traverse(preferences)
  return addresses
}

/**
 * LocalUserPreferences API
 */
const LocalUserPreferences = {
  /**
   * Get a preference value by address
   * @param {string} address - Dot notation address
   * @returns {*} - Value at the address or undefined
   */
  get(address) {
    const preferences = loadPreferences()
    return getValueByAddress(address, preferences)
  },

  /**
   * Set a preference value by address
   * @param {string} address - Dot notation address
   * @param {*} value - Value to set
   */
  set(address, value) {
    const preferences = loadPreferences()
    const updated = setValueByAddress(address, value, preferences)
    savePreferences(updated)
  },

  /**
   * Delete a preference by address
   * @param {string} address - Dot notation address
   */
  delete(address) {
    const preferences = loadPreferences()
    const updated = deleteValueByAddress(address, preferences)
    savePreferences(updated)
  },

  /**
   * Clear preferences by pattern or array of addresses
   * @param {string|Array<string>} patterns - Wildcard pattern, address, or array of addresses
   */
  clear(patterns) {
    if (!patterns) {
      // Clear all preferences
      savePreferences({})
      return
    }

    const preferences = loadPreferences()
    let updated = { ...preferences }

    if (typeof patterns === "string") {
      patterns = [patterns]
    }

    for (const pattern of patterns) {
      if (pattern.includes("*")) {
        // Handle wildcard pattern
        const matchingAddresses = getMatchingAddresses(pattern, updated)
        for (const address of matchingAddresses) {
          updated = deleteValueByAddress(address, updated)
        }
      } else {
        // Handle specific address
        updated = deleteValueByAddress(pattern, updated)
      }
    }

    savePreferences(updated)
  },

  /**
   * List all preference addresses and their values
   * @param {string} [prefix] - Optional prefix to filter addresses
   * @returns {Array<{address: string, value: *}>} - Array of preference entries
   */
  list(prefix = "") {
    const preferences = loadPreferences()
    const entries = []

    const traverse = (obj, currentPath = "") => {
      for (const key in obj) {
        const fullPath = currentPath ? `${currentPath}.${key}` : key
        
        if (!prefix || fullPath.startsWith(prefix)) {
          entries.push({
            address: fullPath,
            value: obj[key]
          })
        }

        if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
          traverse(obj[key], fullPath)
        }
      }
    }

    traverse(preferences)
    return entries.sort((a, b) => a.address.localeCompare(b.address))
  },

  /**
   * Subscribe to preference changes
   * @param {Function} callback - Callback function to receive updates
   * @returns {Function} - Unsubscribe function
   */
  subscribe(callback) {
    const token = PubSub.subscribe("local-user-preferences-changed", (msg, data) => {
      callback(data)
    })

    return () => {
      PubSub.unsubscribe(token)
    }
  },

  /**
   * Get all preferences as a plain object
   * @returns {Object} - All preferences
   */
  getAll() {
    return loadPreferences()
  },

  /**
   * Set a default value for a preference if it doesn't already exist
   * @param {string} address - Dot notation address
   * @param {*} defaultValue - Default value to set if preference doesn't exist
   */
  setDefault(address, defaultValue) {
    const currentValue = this.get(address)
    if (currentValue === undefined) {
      this.set(address, defaultValue)
    }
  }
}

export default LocalUserPreferences